/*
 * Platform admin: companies, company insights, client usage, administrators,
 * administrator roles and the modules catalogue.
 *
 * Companies come from mock/admin.js so the tenant pages built on sample data
 * (tenants/show.vue) and these screens talk about the same four companies.
 * Insights and usage carry the API's fields only (CompanySnapshot,
 * ClientUsageReport): users, roles, approvals, documents and sign-ins.
 */
import { tenants as sampleTenants, platformPlans, platformModules, users as companyUsers } from "@/mock/admin";
import { ok, created, fail, notFound, invalid, paginate, pageMeta, matches, nextId, hoursAgo, daysAgo, now } from "./util";

const conflict = (message) => fail(message, null, 409);
const planName = (key) => platformPlans.find((p) => p.key === key)?.name ?? key ?? null;

// Same numbers on every reload: a small hash stands in for randomness.
const seeded = (...parts) => {
  let h = 2166136261;
  for (const c of parts.join("|")) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  return ((h >>> 0) % 10000) / 10000;
};

/* ------------------------------------------------------------------ */
/* Companies                                                            */
/* ------------------------------------------------------------------ */

const isoDaysAgo = (n) => daysAgo(n);
const dateOnly = (iso) => (iso ? String(iso).slice(0, 10) : null);
const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 864e5);

let addressSeq = 100;
const toAddress = (address) => ({ id: ++addressSeq, address, url: `https://${address}` });

// Sample tenants in the shape TenantResource returns.
const companies = sampleTenants.map((t) => {
  const createdAgo = daysBetween(t.created_at, new Date().toISOString().slice(0, 10));
  return {
    id: t.id,
    code: t.code,
    addresses: t.addresses.map(toAddress),
    display_name: t.name,
    status: t.status,
    logo_url: null,
    identity: {
      legal_name: t.name,
      trading_name: t.name.replace(/ (Ltd|Society)$/, ""),
      slug: t.code.toLowerCase(),
      registration_number: `PVT-${String(t.id * 7349).padStart(6, "0")}`,
      industry: t.industry,
      description: `${t.industry} company in ${t.city}.`,
      employee_count: t.usage.users,
    },
    statutory: { kra_pin: t.kra_pin === "—" ? null : t.kra_pin, nssf_number: null, shif_number: null, nita_number: null, business_permit_number: null },
    contact: { person_name: t.contact.name, person_title: "CRM owner", email: t.contact.email, phone: t.contact.phone, alternate_phone: null, website: null },
    location: { physical_address: `${t.city} CBD`, postal_address: null, city: t.city, country: t.country, region: null, latitude: null, longitude: null },
    branding: { logo_url: null, primary_color: "#22c55e", secondary_color: "#10b981" },
    commercial: { plan: planName(t.plan), contract_start_date: dateOnly(t.created_at), contract_end_date: dateOnly(daysAgo(-(365 - (createdAgo % 365)))) },
    documents: [
      { id: t.id * 10 + 1, document_type: "contract", name: `${t.code}-signed-contract.pdf`, mime_type: "application/pdf", size_bytes: 248113, issued_on: dateOnly(t.created_at), expires_on: null, status: "valid", is_available: true, created_at: isoDaysAgo(createdAgo) },
      { id: t.id * 10 + 2, document_type: "kra_certificate", name: `${t.code}-kra-pin.pdf`, mime_type: "application/pdf", size_bytes: 91022, issued_on: dateOnly(t.created_at), expires_on: null, status: "valid", is_available: true, created_at: isoDaysAgo(createdAgo) },
    ],
    activated_at: t.status === "pending_setup" ? null : isoDaysAgo(createdAgo),
    created_at: isoDaysAgo(createdAgo),
    // Not part of TenantResource: what the usage and insight figures are built from.
    _usage: t.usage,
  };
});

const present = ({ _usage, ...t }) => t;
const findCompany = (id) => companies.find((t) => String(t.id) === String(id));
const normalise = (address) => String(address ?? "").trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/+$/, "");
const addressTaken = (address) => companies.some((t) => t.addresses.some((a) => a.address === address));

const REQUIRED = {
  legal_name: "The legal name field is required.",
  kra_pin: "The KRA PIN field is required.",
  contact_person_name: "The contact person name field is required.",
  contact_email: "The contact email field is required.",
  address: "The address field is required.",
  contact_phone: "The contact phone field is required.",
  physical_address: "The physical address field is required.",
  city: "The city field is required.",
  contract: "The signed contract is required to onboard a company.",
};

const createCompany = (body) => {
  const errors = {};
  Object.entries(REQUIRED).forEach(([key, message]) => {
    if (!body[key]) errors[key] = [message];
  });
  const address = normalise(body.address);
  if (body.kra_pin && companies.some((t) => t.statutory.kra_pin === body.kra_pin)) errors.kra_pin = ["A company with this KRA PIN has already been onboarded."];
  if (address && addressTaken(address)) errors.address = [`${address} already belongs to another company.`];
  if (Object.keys(errors).length) return invalid(errors);

  const id = nextId(companies);
  const company = {
    id,
    code: `CRM${String(id).padStart(3, "0")}`,
    addresses: [toAddress(address)],
    display_name: body.trading_name || body.legal_name,
    status: "pending_setup",
    logo_url: null,
    identity: {
      legal_name: body.legal_name, trading_name: body.trading_name ?? null, slug: `crm${String(id).padStart(3, "0")}`,
      registration_number: body.registration_number ?? null, industry: body.industry ?? null, description: body.description ?? null,
      employee_count: body.employee_count ? Number(body.employee_count) : null,
    },
    statutory: {
      kra_pin: body.kra_pin, nssf_number: body.nssf_number ?? null, shif_number: body.shif_number ?? null,
      nita_number: body.nita_number ?? null, business_permit_number: body.business_permit_number ?? null,
    },
    contact: {
      person_name: body.contact_person_name, person_title: body.contact_person_title ?? null, email: body.contact_email,
      phone: body.contact_phone, alternate_phone: body.alternate_phone ?? null, website: body.website ?? null,
    },
    location: {
      physical_address: body.physical_address, postal_address: body.postal_address ?? null, city: body.city, country: body.country ?? null,
      region: body.region ?? null, latitude: body.latitude ?? null, longitude: body.longitude ?? null,
    },
    branding: { logo_url: null, primary_color: body.primary_color ?? null, secondary_color: body.secondary_color ?? null },
    commercial: { plan: body.plan ?? null, contract_start_date: body.contract_start_date ?? null, contract_end_date: body.contract_end_date ?? null },
    documents: ["contract", "certificate_of_incorporation", "kra_certificate", "business_permit"]
      .filter((key) => body[key])
      .map((key, i) => ({
        id: id * 10 + i + 1, document_type: key, name: body[key].name ?? `${key}.pdf`, mime_type: "application/pdf",
        size_bytes: body[key].size ?? 0, issued_on: null, expires_on: null, status: "valid", is_available: false, created_at: now(),
      })),
    activated_at: null,
    created_at: now(),
    _usage: { users: 0, apps: 0, api_calls: 0, storage: 0, records: {} },
  };
  companies.push(company);

  return created(
    present(company),
    `${company.display_name} is being set up. ${company.contact.email} will be emailed an invitation once it is ready, usually within a few minutes.`
  );
};

/* ------------------------------------------------------------------ */
/* Company insights (the admin home)                                    */
/* ------------------------------------------------------------------ */

const ROLE_LABELS = ["Sales", "Sales Manager", "Customer Success", "Implementation", "Support", "Marketing", "Finance", "Management", "CTO/Admin", "Audit/Risk"];

// Split a total across labels by fixed weights, so the parts add up.
const spread = (total, labels, key) => {
  const weights = labels.map((l) => 0.4 + seeded(key, l));
  const sum = weights.reduce((a, b) => a + b, 0);
  const parts = weights.map((w) => Math.floor((w / sum) * total));
  parts[0] += total - parts.reduce((a, b) => a + b, 0);
  return labels.map((label, i) => ({ label, total: parts[i] })).filter((r) => r.total > 0).sort((a, b) => b.total - a.total);
};

const snapshot = (company) => {
  const u = company._usage;
  const users = u.users;
  const seed = company.code;
  const was = Math.max(0, users - Math.round(seeded(seed, "was") * 3));
  const joined = Math.round(seeded(seed, "joined") * 4);
  const invited = Math.round(seeded(seed, "invited") * 3);
  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - (11 - i));
    return d;
  });

  let running = Math.max(1, Math.round(users * 0.7));
  const trend = months.map((d, i) => {
    const join = i === 11 ? users - running : Math.round(seeded(seed, "j", i) * Math.max(2, users * 0.06));
    running = i === 11 ? users : Math.min(users, running + join);
    return { label: d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" }), total: running, joined: Math.max(0, join) };
  });

  const byRole = spread(users, ROLE_LABELS, `${seed}-roles`).map((r) => ({ label: r.label, count: r.total }));

  return {
    tiles: {
      users: { value: users, was },
      joined_this_month: joined,
      users_invited: invited,
      users_suspended: Math.round(seeded(seed, "susp") * 2),
      approvals_pending: Math.round(seeded(seed, "appr") * 9),
      documents: Math.round((u.records?.Customers ?? 50) * 1.6),
    },
    by_role: byRole,
    trend,
    usage: {
      users,
      users_invited: invited,
      sign_ins_30_days: Math.round(users * (8 + seeded(seed, "si") * 14)),
      people_signed_in_30_days: Math.round(users * (0.55 + seeded(seed, "ps") * 0.4)),
      last_sign_in: company.status === "active" ? hoursAgo(1 + seeded(seed, "last") * 20) : daysAgo(21),
    },
  };
};

const dashboard = (query) => {
  const active = companies.filter((t) => t.status === "active").sort((a, b) => a.code.localeCompare(b.code));
  const total = active.length;
  if (!total) return ok({ tenant: null, stats: null, total_tenants: 0 }, "No active companies yet.");

  let page;
  if (query.tenant) {
    const code = String(query.tenant).toUpperCase();
    page = active.findIndex((t) => t.code === code) + 1;
    if (!page) return fail("Active company was not found.", { details: { id: code } }, 404);
  } else {
    page = Math.min(Math.max(1, Number(query.page) || 1), total);
  }

  const t = active[page - 1];
  return ok({
    tenant: {
      id: t.id, code: t.code, name: t.display_name, logo_url: t.logo_url, plan: t.commercial.plan, status: t.status,
      activated_at: dateOnly(t.activated_at), contract_end_date: t.commercial.contract_end_date, sign_in_url: t.addresses[0]?.url ?? null,
    },
    stats: snapshot(t),
    current_page: page,
    last_page: total,
    total_tenants: total,
    has_next: page < total,
    has_previous: page > 1,
  });
};

/* ------------------------------------------------------------------ */
/* Client usage                                                         */
/* ------------------------------------------------------------------ */

const periodDays = (query) => {
  const to = query.to ? new Date(`${query.to}T00:00:00`) : new Date();
  const from = query.from ? new Date(`${query.from}T00:00:00`) : new Date(to.getTime() - 29 * 864e5);
  const days = [];
  for (let d = new Date(from); d <= to && days.length < 367; d.setDate(d.getDate() + 1)) days.push(new Date(d));
  return days;
};
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// One company's sign-ins for each day of the period; nothing while suspended or setting up.
const dailyActivity = (company, days) =>
  days.map((d) => {
    const key = isoDay(d);
    const weekend = d.getDay() === 0 || d.getDay() === 6;
    const live = company.status === "active";
    const people = live ? Math.round(company._usage.users * (weekend ? 0.08 : 0.45 + seeded(company.code, key) * 0.35)) : 0;
    const signIns = live ? Math.round(people * (1.3 + seeded(company.code, key, "s"))) : 0;
    return { date: key, label: d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }), sign_ins: signIns, people, failed: live ? Math.round(seeded(company.code, key, "f") * 3) : 0 };
  });

const roleSplit = (rows, key) =>
  spread(rows.reduce((n, r) => n + r.sign_ins, 0), ROLE_LABELS, `${key}-usage`).map((r) => ({
    label: r.label,
    sign_ins: r.total,
    person_days: Math.round(r.total / 1.7),
  }));

const usageRow = (company, days) => {
  const act = dailyActivity(company, days);
  const u = company._usage;
  const seed = company.code;
  const usersActive = company.status === "pending_setup" ? 0 : u.users;
  const people30 = company.status === "active" ? Math.round(usersActive * (0.55 + seeded(seed, "ps") * 0.4)) : 0;
  return {
    id: company.id, code: company.code, name: company.display_name, plan: company.commercial.plan, status: company.status,
    counted: company.status !== "pending_setup",
    sign_ins: act.reduce((n, r) => n + r.sign_ins, 0),
    failed_sign_ins: act.reduce((n, r) => n + r.failed, 0),
    daily_people: Math.round((act.reduce((n, r) => n + r.people, 0) / Math.max(1, act.length)) * 10) / 10,
    active_days: act.filter((r) => r.sign_ins > 0).length,
    users_total: usersActive + 2,
    users_active: usersActive,
    users_invited: Math.round(seeded(seed, "invited") * 3),
    people_active_7: Math.round(people30 * 0.8),
    people_active_30: people30,
    adoption: usersActive ? Math.round((people30 / usersActive) * 1000) / 10 : 0,
    users_suspended: Math.round(seeded(seed, "susp") * 2),
    joined_30: Math.round(seeded(seed, "joined") * 4),
    approvals_pending: Math.round(seeded(seed, "appr") * 9),
    approvals_30: Math.round(seeded(seed, "a30") * 40),
    documents: Math.round((u.records?.Customers ?? 50) * 1.6),
    last_sign_in: company.status === "active" ? hoursAgo(1 + seeded(seed, "last") * 20) : company.status === "suspended" ? daysAgo(21) : null,
    computed_at: hoursAgo(0.4),
    _activity: act,
  };
};

const sumBy = (rows, key) => rows.reduce((n, r) => n + (r[key] ?? 0), 0);

const compare = (query) => {
  const days = periodDays(query);
  const rows = companies.map((t) => usageRow(t, days)).sort((a, b) => b.sign_ins - a.sign_ins);
  const byDay = days.map((d, i) => ({
    date: isoDay(d),
    label: d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
    sign_ins: rows.reduce((n, r) => n + r._activity[i].sign_ins, 0),
    people: rows.reduce((n, r) => n + r._activity[i].people, 0),
  }));
  const companiesOut = rows.map(({ _activity, ...r }) => r);

  return ok({
    from: isoDay(days[0]),
    to: isoDay(days[days.length - 1]),
    totals: {
      companies: rows.length,
      companies_active: rows.filter((r) => r.sign_ins > 0).length,
      sign_ins: sumBy(rows, "sign_ins"),
      failed_sign_ins: sumBy(rows, "failed_sign_ins"),
      daily_people: Math.round((byDay.reduce((n, d) => n + d.people, 0) / Math.max(1, days.length)) * 10) / 10,
      users_total: sumBy(rows, "users_total"),
      users_active: sumBy(rows, "users_active"),
      people_active_30: sumBy(rows, "people_active_30"),
    },
    by_day: byDay,
    by_role: roleSplit(byDay, "all"),
    companies: companiesOut,
    computed_at: hoursAgo(0.4),
    stale: false,
  });
};

const companyUsage = (id, query) => {
  const company = findCompany(id);
  if (!company) return notFound("Company was not found.");
  const days = periodDays(query);
  const { _activity, ...row } = usageRow(company, days);
  const byDay = _activity.map(({ failed, ...d }) => d);
  return ok({ company: row, by_day: byDay, by_role: roleSplit(byDay, company.code) });
};

// Who signed in: Demulla's own users, made-up colleagues for the others.
const OTHER_NAMES = ["Philip Kiptoo", "Esther Nduta", "Hassan Ali", "Amina Said", "Moses Ssali", "Ruth Namuli", "Collins Were", "Purity Gathoni"];
const AGENTS = ["Windows · Chrome", "macOS · Safari", "Android · Chrome", "iOS · Safari", "Windows · Edge"];

const signIns = (id, query) => {
  const company = findCompany(id);
  if (!company) return notFound("Company was not found.");
  const domain = company.contact.email.split("@")[1];
  const people = company.code === "CRM001"
    ? companyUsers.filter((u) => u.status === "active").map((u) => ({ name: u.name, email: u.email, roles: u.roles.join(", ") }))
    : OTHER_NAMES.map((name, i) => ({ name, email: `${name.split(" ")[0].toLowerCase()}@${domain}`, roles: ROLE_LABELS[i % ROLE_LABELS.length] }));

  const days = periodDays(query);
  const rows = [];
  if (company.status === "active") {
    [...days].reverse().forEach((d, di) => {
      const count = d.getDay() === 0 || d.getDay() === 6 ? 1 : 3;
      for (let k = 0; k < count; k++) {
        const p = people[Math.floor(seeded(company.code, di, k) * people.length)];
        const failed = seeded(company.code, di, k, "f") < 0.08;
        const at = new Date(d);
        at.setHours(17 - k * 3, Math.floor(seeded(di, k) * 60));
        rows.push({
          id: rows.length + 1, at: at.toISOString(), name: failed ? null : p.name, email: p.email, roles: failed ? null : p.roles, ok: !failed,
          reason: failed ? "Wrong password" : null, ip: `41.90.${(di * 7 + k) % 255}.${(di * 13 + k * 5) % 255}`, device: AGENTS[(di + k) % AGENTS.length],
        });
      }
    });
  }

  const shown = rows
    .filter((r) => (query.outcome === "success" ? r.ok : query.outcome === "failed" ? !r.ok : true))
    .filter((r) => matches(query.search, r.name, r.email, r.ip));
  const page = paginate(shown, query, 25);
  return ok({ sign_ins: page.data, meta: { current_page: page.current_page, last_page: page.last_page, total: page.total } });
};

/* ------------------------------------------------------------------ */
/* Administrators and their roles                                       */
/* ------------------------------------------------------------------ */

const SUPER = "system-admin";
const SUPER_ONLY = [
  "plans.manage", "catalogue.manage", "billing.manage", "website.manage", "platform-integrations.manage",
  "admins.create", "admins.update", "admins.delete", "admin-roles.view", "admin-roles.manage", "impersonation.write",
];
const GROUPS = {
  Companies: ["tenants", "tenant"],
  "Trial applications": ["trial-applications"],
  "Client usage": ["client-usage"],
  Website: ["website-traffic", "website"],
  "Plans & billing": ["plans", "billing"],
  "Modules catalogue": ["catalogue"],
  Integrations: ["platform-integrations"],
  "API documentation": ["api-docs"],
  Administrators: ["admins", "admin-roles"],
  Support: ["impersonation"],
  "Logs & exports": ["logs", "audit", "exports"],
};
// AdminPermissionSeeder.
const ADMIN_PERMISSIONS = [
  ["tenants.view", "Can view tenants"], ["tenants.create", "Can create tenant"], ["tenants.update", "Can edit tenant"],
  ["tenants.activate", "Can activate tenant"], ["tenants.suspend", "Can suspend tenant"], ["tenants.deactivate", "Can deactivate tenant"],
  ["tenant.users.view", "Can view tenant users"],
  ["impersonation.view", "Can view impersonation sessions"], ["impersonation.start", "Can start impersonation"],
  ["impersonation.write", "Can make changes while impersonating"], ["impersonation.end", "Can end impersonation"],
  ["catalogue.view", "Can view the modules catalogue"], ["catalogue.manage", "Can describe, order and sync the modules catalogue"],
  ["billing.view", "Can view payment schedules and payment methods"], ["billing.manage", "Can set up payment schedules and payment methods"],
  ["plans.view", "Can view plans"], ["plans.manage", "Can create and change plans, their prices and modules"],
  ["trial-applications.view", "Can see free-trial applications from the website"], ["trial-applications.review", "Can approve or decline free-trial applications"],
  ["website.view", "Can view the website settings"],
  ["client-usage.view", "Can compare companies' sign-ins, users and other usage figures"],
  ["client-usage.people", "Can see who signed in to each company, with their roles and addresses"],
  ["website-traffic.view", "Can see website visitors, their addresses and where they came from"],
  ["website.manage", "Can change the contact details and links shown on the website"],
  ["platform-integrations.view", "Can view the website connection and other platform integrations"],
  ["platform-integrations.manage", "Can issue tokens, set addresses and refresh the website"],
  ["api-docs.view", "Can see who may read the API documentation"],
  ["api-docs.manage", "Can give, change and take away API documentation logins"],
  ["admins.view", "Can view administrators"], ["admins.create", "Can create administrator"], ["admins.update", "Can edit administrator"],
  ["admins.delete", "Can delete administrator"],
  ["admin-roles.view", "Can view administrator roles and their rights"], ["admin-roles.manage", "Can create, change and remove administrator roles"],
  ["audit.view", "Can view audit logs"], ["logs.view", "Can view activity logs"], ["exports.view", "Can view own exports"], ["exports.create", "Can export data"],
].map(([name, label]) => ({ name, label }));
const allRights = ADMIN_PERMISSIONS.map((p) => p.name);

const adminRoles = [
  { id: 1, name: SUPER, label: "Super Admin", description: "Holds every right, including setting up plans, modules, billing, the website and who administers the platform.", permissions: allRights },
  {
    id: 2, name: "support", label: "Support",
    description: "Helps companies: sees them, their users and how they use DeeCRM360, and reads the logs. No billing, plans or settings.",
    permissions: ["tenants.view", "tenant.users.view", "client-usage.view", "client-usage.people", "trial-applications.view", "impersonation.view", "logs.view", "audit.view", "exports.view", "exports.create"],
  },
  {
    id: 3, name: "sales", label: "Sales & Onboarding",
    description: "Brings companies in: reviews trial applications, sets up new companies and follows website traffic.",
    permissions: ["trial-applications.view", "trial-applications.review", "tenants.view", "tenants.create", "plans.view", "catalogue.view", "client-usage.view", "website-traffic.view", "exports.view", "exports.create"],
  },
  {
    id: 4, name: "billing", label: "Billing",
    description: "Follows plans, payment schedules and payment methods, and which companies are on which plan.",
    permissions: ["billing.view", "plans.view", "tenants.view", "client-usage.view", "exports.view", "exports.create"],
  },
  {
    id: 5, name: "read-only", label: "Read-only",
    description: "Sees companies, usage and the logs; changes nothing.",
    permissions: ["tenants.view", "client-usage.view", "plans.view", "catalogue.view", "billing.view", "website.view", "logs.view", "audit.view"],
  },
];

// Id 1 is the signed-in prototype admin ("Platform Admin").
const admins = [
  { id: 1, name: "Platform Admin", email: "demo@deecrm360.test", status: "active", role: SUPER, invited_at: daysAgo(420), last_sign_in_at: hoursAgo(0.2), created_at: daysAgo(420) },
  { id: 2, name: "Otieno Kamau", email: "otieno.kamau@deecrm360.com", status: "active", role: SUPER, invited_at: daysAgo(418), last_sign_in_at: hoursAgo(5), created_at: daysAgo(418) },
  { id: 3, name: "Achieng Mumbi", email: "achieng.mumbi@deecrm360.com", status: "active", role: "support", invited_at: daysAgo(300), last_sign_in_at: hoursAgo(26), created_at: daysAgo(300) },
  { id: 4, name: "Wafula Simiyu", email: "wafula.simiyu@deecrm360.com", status: "active", role: "sales", invited_at: daysAgo(180), last_sign_in_at: daysAgo(3), created_at: daysAgo(180) },
  { id: 5, name: "Nekesa Auma", email: "nekesa.auma@deecrm360.com", status: "pending", role: "billing", invited_at: daysAgo(2), last_sign_in_at: null, created_at: daysAgo(2) },
  { id: 6, name: "Kibet Langat", email: "kibet.langat@deecrm360.com", status: "inactive", role: "read-only", invited_at: daysAgo(250), last_sign_in_at: daysAgo(61), created_at: daysAgo(250), activated: true },
];
const ME = 1;

const roleOf = (name) => adminRoles.find((r) => r.name === name);
const presentAdmin = ({ activated, ...a }) => ({
  id: a.id, name: a.name, email: a.email, status: a.status,
  role: a.role ?? null, role_label: roleOf(a.role)?.label ?? null,
  is_super_admin: a.role === SUPER, is_me: a.id === ME,
  invited_at: a.invited_at, last_sign_in_at: a.last_sign_in_at, created_at: a.created_at,
});
const findAdmin = (id) => admins.find((a) => String(a.id) === String(id));
const inviteUrl = (a) => `https://admin.deecrm360.com/auth/invite?token=${btoa(`${a.id}:${Date.now()}`).replace(/=+$/, "")}`;

const lastSuper = (a) => a.role === SUPER && a.status === "active" && !admins.some((x) => x.id !== a.id && x.role === SUPER && x.status === "active");
const guard = (a, what, self = true) => {
  if (self && a.id === ME) return conflict(`You cannot ${what}. Ask another super admin.`);
  return null;
};
const keepSuper = (a, what) =>
  lastSuper(a) ? conflict(`${a.name} is the only active super admin. Make someone else a super admin before you ${what}.`) : null;

const roleGroups = () =>
  [...Object.keys(GROUPS), "Other"]
    .map((label) => ({
      label,
      permissions: ADMIN_PERMISSIONS.filter((p) => {
        const prefix = p.name.split(".")[0];
        const group = Object.keys(GROUPS).find((g) => GROUPS[g].includes(prefix)) ?? "Other";
        return group === label;
      })
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((p) => ({ ...p, super_admin_only: SUPER_ONLY.includes(p.name) })),
    }))
    .filter((g) => g.permissions.length);

const validRole = (body, existing = null) => {
  const label = String(body.label ?? "").trim();
  if (!label) return invalid({ label: ["Give the role a name."] });
  if (adminRoles.some((r) => r.label.toLowerCase() === label.toLowerCase() && r.id !== existing?.id)) return invalid({ label: ["There is already a role with that name."] });
  const permissions = Array.isArray(body.permissions) ? body.permissions : [];
  if (permissions.some((p) => !allRights.includes(p))) return invalid({ "permissions.0": ["One of the rights chosen does not exist."] });
  const blocked = permissions.filter((p) => SUPER_ONLY.includes(p));
  if (blocked.length) {
    const labels = ADMIN_PERMISSIONS.filter((p) => blocked.includes(p.name)).map((p) => p.label).join("; ");
    return fail(`Only super admins can hold these rights: ${labels}.`, null, 422);
  }
  return { label, description: body.description || null, permissions };
};

const editableRole = (id) => {
  const role = adminRoles.find((r) => String(r.id) === String(id));
  if (!role) return notFound("Role was not found.");
  if (role.name === SUPER) return conflict("The super admin role always holds every right and cannot be changed or removed.");
  return role;
};

/* ------------------------------------------------------------------ */
/* Modules catalogue                                                    */
/* ------------------------------------------------------------------ */

const ACTION_LABELS = { view: "View", create: "Create", update: "Edit", delete: "Delete", approve: "Approve", manage: "Manage", export: "Export", assign: "Assign", upload: "Upload", configure: "Configure", propose: "Propose" };
const rightsFor = (prefix, noun, actions) => actions.map((a) => ({ name: `${prefix}.${a}`, label: `${ACTION_LABELS[a] ?? a} ${noun}` }));

// [module key, name, description, website tagline, submodules: [key, name, [[prefix, noun, actions]]]]
const CATALOGUE = [
  ["company", "Company", "The company's own profile, branding and sign-in addresses.", null, [
    ["company-profile", "Company Profile", [["company", "company profile", ["view", "update"]]]],
  ]],
  ["users", "Users", "Who can sign in, with which roles and over which records.", null, [
    ["users", "Users", [["users", "users", ["view", "create", "update", "delete"]]]],
    ["sensitive-fields", "Sensitive Fields", [["field", "sensitive fields", ["view"]]]],
  ]],
  ["prospects", "Prospects", "Companies worth approaching, before anybody has shown interest.", "Find and qualify the companies worth selling to", [
    ["prospects", "Prospects", [["prospects", "prospects", ["view", "create", "update", "delete", "assign", "export"]]]],
  ]],
  ["leads", "Leads & Pipeline", "Interest turned into leads and opportunities, stage by stage.", "Every deal, every stage, one pipeline", [
    ["leads", "Leads", [["leads", "leads", ["view", "create", "update", "delete", "assign"]]]],
    ["opportunities", "Opportunities", [["opportunities", "opportunities", ["view", "create", "update", "delete", "approve"]]]],
  ]],
  ["quotations", "Quotations", "Priced offers, discounts and their approval.", "Quotes out the door, discounts signed off", [
    ["quotations", "Quotations", [["quotations", "quotations", ["view", "create", "update", "approve", "delete"]]]],
  ]],
  ["demos", "Demos", "Product demonstrations booked, held and followed up.", null, [
    ["demos", "Demos", [["demos", "demos", ["view", "create", "update"]]]],
  ]],
  ["customers", "Customers", "Customer accounts and the people in them.", "One record per customer, everyone who works with them", [
    ["customers", "Customers", [["customers", "customers", ["view", "create", "update", "export"]]]],
    ["contacts", "Contacts", [["contacts", "contacts", ["view", "create", "update", "delete"]]]],
  ]],
  ["contracts", "Contracts", "Signed agreements, their terms and documents.", null, [
    ["contracts", "Contracts", [["contracts", "contracts", ["view", "create", "update", "approve"]]]],
  ]],
  ["onboarding", "Onboarding", "Implementation projects from kick-off to sign-off.", "From signed contract to live customer", [
    ["onboarding", "Onboarding", [["onboarding", "onboarding projects", ["view", "create", "update", "approve"]]]],
  ]],
  ["success", "Customer Success", "Health scores, reviews and success plans.", "Know which customers are thriving", [
    ["success", "Customer Success", [["success", "success plans", ["view", "create", "update"]]]],
  ]],
  ["support", "Support & Ticket API", "Tickets from people and from connected systems, against SLAs.", "Tickets from every channel, on time", [
    ["tickets", "Tickets", [["tickets", "tickets", ["view", "create", "update", "assign"]]]],
    ["ticket-clients", "Ticket API clients", [["ticket-clients", "ticket API clients", ["view", "manage"]]]],
  ]],
  ["renewals", "Renewals", "Contracts coming up for renewal and their outcome.", null, [
    ["renewals", "Renewals", [["renewals", "renewals", ["view", "update", "approve"]]]],
  ]],
  ["churn", "Churn & Exit", "Customers at risk, and leaving properly when they do.", null, [
    ["churn", "Churn", [["churn", "churn risks", ["view", "update"]]]],
    ["exits", "Exits", [["exits", "exits", ["view", "create", "approve"]]]],
  ]],
  ["documents", "Documents", "Files kept against customers and the company.", null, [
    ["documents", "Documents", [["documents", "documents", ["view", "upload", "delete"]]]],
    ["folders", "Folders", [["folders", "folders", ["view", "create"]]]],
  ]],
  ["reporting", "Reporting", "Dashboards, reports and exports.", "The numbers sales and success run on", [
    ["reports", "Reports", [["reports", "reports", ["view", "export"]]]],
    ["exports", "Exports", [["exports", "exports", ["view", "create"]]]],
    ["dashboard", "Dashboard", [["dashboard", "dashboard", ["view"]]]],
  ]],
  ["system-administration", "System Administration", "Roles, approvals, integrations, settings and the audit trail.", null, [
    ["roles", "Roles & Permissions", [["roles", "roles", ["view", "create", "update", "delete"]], ["role-permissions", "role permissions", ["view", "propose"]], ["role-assignments", "role assignments", ["view", "propose"]]]],
    ["approvals", "Approvals", [["approvals", "approvals", ["view", "approve"]], ["approval-workflows", "approval workflows", ["view", "configure"]]]],
    ["data-protection", "Data Protection", [["sensitive-fields", "sensitive fields", ["view", "configure"]]]],
    ["integrations", "Integrations", [["integrations", "integrations", ["view", "manage"]]]],
    ["settings", "Settings", [["settings", "settings", ["view", "update"]]]],
    ["audit", "Audit", [["logs", "logs", ["view"]]]],
  ]],
];

const coreKeys = new Set(platformModules.filter((m) => m.plans.length === 3).map((m) => m.key));
const ALWAYS_CORE = ["company", "users", "documents", "system-administration", "reporting", "customers"];

let subSeq = 0;
const modules = CATALOGUE.map(([key, name, description, tagline, subs], i) => ({
  id: i + 1, key, name, description, sort_order: (i + 1) * 10,
  is_core: ALWAYS_CORE.includes(key) || coreKeys.has(key),
  is_active: true,
  website_visible: !["company", "users", "system-administration"].includes(key),
  tagline,
  intro: tagline ? `${name} in DeeCRM360: ${description.charAt(0).toLowerCase()}${description.slice(1)}` : null,
  steps: tagline
    ? [
        { title: "Capture", text: `Record ${name.toLowerCase()} as they happen, from the screen or the API.`, illustration: "form" },
        { title: "Work it", text: "Assign an owner, follow up and move it on stage by stage.", illustration: "pipeline" },
        { title: "Sign off", text: "Approvals go to the right person, in the order the company set.", illustration: "approve" },
        { title: "See the numbers", text: "Dashboards and exports show how the team is doing.", illustration: "chart" },
      ]
    : [],
  features: tagline ? [{ title: "Company-specific stages", text: "Each company names its own stages and reasons." }, { title: "Ownership", text: "Every record has an owner, team and supervisor." }] : [],
  audience: tagline ? [{ role: "Sales", text: "Work their own records day to day." }, { role: "Managers", text: "See the team's pipeline and step in." }] : [],
  submodules: subs.map(([skey, sname, rights], j) => ({
    id: ++subSeq, key: skey, name: sname, description: j === 0 ? description : null, sort_order: (j + 1) * 10, is_active: true,
    rights: rights.flatMap(([prefix, noun, actions]) => rightsFor(prefix, noun, actions)),
    prefixes: rights.map(([prefix]) => prefix),
  })),
}));

// A leftover from the HR heritage: kept, marked as no longer in the system.
modules.push({
  id: modules.length + 1, key: "payroll", name: "Payroll", description: null, sort_order: 990, is_core: false, is_active: false,
  website_visible: false, tagline: null, intro: null, steps: [], features: [], audience: [],
  submodules: [{ id: ++subSeq, key: "payroll", name: "Payroll", description: null, sort_order: 10, is_active: false, rights: [], prefixes: ["payroll"] }],
});

const ILLUSTRATIONS = {
  form: "Filling in a form", review: "Reviewing", approve: "Approving", notify: "A notification", calendar: "Calendar", clock: "Clock",
  invoice: "Invoice", pipeline: "Sales pipeline", chart: "Chart", folder: "Folder of documents", upload: "Uploading a file",
  "org-tree": "Organisation chart", people: "A group of people", handshake: "Welcome", target: "Target", award: "Award",
  megaphone: "Announcement", search: "Searching", shield: "Protection", settings: "Settings", checklist: "Checklist",
  laptop: "Working on a laptop", location: "Location", warning: "Warning",
};
let syncedAt = hoursAgo(30);

const catalogue = () => {
  const active = modules.filter((m) => m.is_active);
  const sorted = [...modules].sort((a, b) => Number(b.is_active) - Number(a.is_active) || a.sort_order - b.sort_order);
  return ok({
    modules: sorted.map((m) => ({ ...m, submodules: [...m.submodules].sort((a, b) => Number(b.is_active) - Number(a.is_active) || a.sort_order - b.sort_order) })),
    counts: {
      modules: active.length,
      core: active.filter((m) => m.is_core).length,
      submodules: active.reduce((n, m) => n + m.submodules.filter((s) => s.is_active).length, 0),
      rights: modules.reduce((n, m) => n + m.submodules.reduce((k, s) => k + s.rights.length, 0), 0),
      retired: modules.filter((m) => !m.is_active).length + modules.reduce((n, m) => n + m.submodules.filter((s) => !s.is_active).length, 0),
    },
    synced_at: syncedAt,
    illustrations: Object.entries(ILLUSTRATIONS).map(([key, label]) => ({ key, label })),
  });
};

const updateModule = (id, body) => {
  const m = modules.find((x) => String(x.id) === String(id));
  if (!m) return notFound("Module was not found.");
  if (!String(body.name ?? "").trim()) return invalid({ name: ["Give the module a name customers will recognise."] });
  const fields = ["name", "description", "sort_order", "is_core", "website_visible", "tagline", "intro", "steps", "features", "audience"];
  fields.forEach((f) => {
    if (f in body) m[f] = body[f];
  });
  return ok(m, `${m.name} saved`);
};

const updateSubmodule = (id, body) => {
  const s = modules.flatMap((m) => m.submodules).find((x) => String(x.id) === String(id));
  if (!s) return notFound("Sub-module was not found.");
  if (!String(body.name ?? "").trim()) return invalid({ name: ["Give the sub-module a name customers will recognise."] });
  Object.assign(s, { name: body.name, description: body.description ?? null, sort_order: Number(body.sort_order ?? s.sort_order) });
  return ok(null, `${s.name} saved`);
};

/* ------------------------------------------------------------------ */
/* Routes                                                               */
/* ------------------------------------------------------------------ */

export default [
  // Companies
  ["get", "admin/tenants", ({ query }) => {
    const rows = companies.filter((t) => matches(query.search, t.display_name, t.code)).map(present);
    const page = paginate(rows, query, 100);
    // TenantResource::collection(...)->response()->getData(true): rows, links and meta.
    return ok({
      data: page.data,
      links: { first: page.first_page_url, last: page.last_page_url, prev: page.prev_page_url, next: page.next_page_url },
      meta: { ...pageMeta(page), from: page.from, to: page.to, path: page.path, links: [] },
    });
  }],
  ["post", "admin/tenants", ({ body }) => createCompany(body)],
  ["post", "admin/tenants/:id/addresses", ({ params, body }) => {
    const t = findCompany(params.id);
    if (!t) return notFound("Company was not found.");
    const address = normalise(body.address);
    if (!address) return invalid({ address: ["The address field is required."] });
    if (!/^[a-z0-9.-]+$/.test(address)) return invalid({ address: ["Enter the address on its own, like crm.amani.co.ke. Letters, digits, hyphens and dots only — no underscores, spaces or https://."] });
    if (address.startsWith("admin.")) return invalid({ address: [`${address} is the platform administration address.`] });
    if (addressTaken(address)) return invalid({ address: [`${address} already belongs to another company.`] });
    t.addresses.push(toAddress(address));
    return ok(present(t), `${t.display_name} can now sign in at ${address}.`);
  }],
  ["delete", "admin/tenants/:id/addresses/:addressId", ({ params }) => {
    const t = findCompany(params.id);
    if (!t) return notFound("Company was not found.");
    const a = t.addresses.find((x) => String(x.id) === String(params.addressId));
    if (!a) return notFound("Sign-in address was not found.");
    if (t.addresses.length === 1) return conflict(`${a.address} is ${t.display_name}'s only sign-in address. Add another before removing it.`);
    t.addresses = t.addresses.filter((x) => x !== a);
    return ok(present(t), `${a.address} removed. Its people now sign in at ${t.addresses[0].address}.`);
  }],
  ["get", "admin/tenants/:id", ({ params }) => {
    const t = findCompany(params.id);
    return t ? ok(present(t)) : notFound("Company was not found.");
  }],

  // Company insights
  ["get", "admin/tenants-dashboard", ({ query }) => dashboard(query)],

  // Client usage
  ["get", "admin/client-usage", ({ query }) => compare(query)],
  ["post", "admin/client-usage/refresh", () => ok({ companies: companies.length, failed: {} }, `Figures recounted for ${companies.length} companies.`)],
  ["get", "admin/client-usage/:id/sign-ins", ({ params, query }) => signIns(params.id, query)],
  ["get", "admin/client-usage/:id", ({ params, query }) => companyUsage(params.id, query)],

  // Administrators
  ["get", "admin/admins", () => {
    const order = { active: 0, pending: 1, inactive: 2 };
    const list = [...admins].sort((a, b) => order[a.status] - order[b.status] || a.name.localeCompare(b.name));
    return ok({
      admins: list.map(presentAdmin),
      roles: [...adminRoles].sort((a, b) => a.label.localeCompare(b.label)).map((r) => ({ name: r.name, label: r.label })),
    });
  }],
  ["post", "admin/admins", ({ body }) => {
    const errors = {};
    if (!String(body.name ?? "").trim()) errors.name = ["The name field is required."];
    if (!String(body.email ?? "").trim()) errors.email = ["The email field is required."];
    else if (admins.some((a) => a.email === String(body.email).toLowerCase())) errors.email = ["The email has already been taken."];
    if (!roleOf(body.role)) errors.role = ["Choose one of the administrator roles."];
    if (Object.keys(errors).length) return invalid(errors);
    const admin = { id: nextId(admins), name: body.name, email: String(body.email).toLowerCase(), status: "pending", role: body.role, invited_at: now(), last_sign_in_at: null, created_at: now() };
    admins.push(admin);
    return created({ ...presentAdmin(admin), invite_url: inviteUrl(admin) }, `${admin.name} has been invited. The invitation was emailed to ${admin.email}.`);
  }],
  ["put", "admin/admins/:id", ({ params, body }) => {
    const a = findAdmin(params.id);
    if (!a) return notFound("Administrator was not found.");
    if (!String(body.name ?? "").trim()) return invalid({ name: ["The name field is required."] });
    if (!roleOf(body.role)) return invalid({ role: ["Choose one of the administrator roles."] });
    if (body.role !== a.role) {
      const refused = guard(a, "change your own role") ?? keepSuper(a, "move them to another role");
      if (refused) return refused;
    }
    Object.assign(a, { name: body.name, role: body.role });
    return ok(presentAdmin(a), `${a.name} saved`);
  }],
  ["post", "admin/admins/:id/deactivate", ({ params }) => {
    const a = findAdmin(params.id);
    if (!a) return notFound("Administrator was not found.");
    const refused = guard(a, "switch yourself off") ?? keepSuper(a, "switch them off");
    if (refused) return refused;
    a.status = "inactive";
    return ok(presentAdmin(a), `${a.name} can no longer sign in`);
  }],
  ["post", "admin/admins/:id/reactivate", ({ params }) => {
    const a = findAdmin(params.id);
    if (!a) return notFound("Administrator was not found.");
    a.status = a.last_sign_in_at || a.activated ? "active" : "pending";
    return ok(presentAdmin(a), a.status === "active" ? `${a.name} can sign in again` : `${a.name} is back to waiting for their invitation`);
  }],
  ["post", "admin/admins/:id/invite", ({ params }) => {
    const a = findAdmin(params.id);
    if (!a) return notFound("Administrator was not found.");
    if (a.status !== "pending") return conflict(`${a.name} has already activated their account. They can reset their password from the sign-in page.`);
    a.invited_at = now();
    return ok({ invite_url: inviteUrl(a) }, `A new invitation was emailed to ${a.email}`);
  }],
  ["delete", "admin/admins/:id", ({ params }) => {
    const a = findAdmin(params.id);
    if (!a) return notFound("Administrator was not found.");
    const refused = guard(a, "remove yourself") ?? keepSuper(a, "remove them");
    if (refused) return refused;
    admins.splice(admins.indexOf(a), 1);
    return ok(null, `${a.name} has been removed`);
  }],

  // Administrator roles
  ["get", "admin/roles", () => {
    const roles = [...adminRoles]
      .sort((a, b) => (a.name === SUPER ? -1 : b.name === SUPER ? 1 : a.label.localeCompare(b.label)))
      .map((r) => ({
        id: r.id, name: r.name, label: r.label, description: r.description,
        is_super_admin: r.name === SUPER,
        permissions: r.name === SUPER ? allRights : [...r.permissions],
        holders: admins.filter((a) => a.role === r.name).length,
      }));
    return ok({ roles, groups: roleGroups() });
  }],
  ["post", "admin/roles", ({ body }) => {
    const data = validRole(body);
    if (Array.isArray(data)) return data;
    const name = data.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    if (adminRoles.some((r) => r.name === name)) return conflict(`There is already a role called ${data.label}.`);
    const role = { id: nextId(adminRoles), name, ...data };
    adminRoles.push(role);
    return created({ id: role.id }, `${role.label} created`);
  }],
  ["put", "admin/roles/:id", ({ params, body }) => {
    const role = editableRole(params.id);
    if (Array.isArray(role)) return role;
    const data = validRole(body, role);
    if (Array.isArray(data)) return data;
    Object.assign(role, data);
    return ok(null, `${role.label} saved`);
  }],
  ["delete", "admin/roles/:id", ({ params }) => {
    const role = editableRole(params.id);
    if (Array.isArray(role)) return role;
    const holders = admins.filter((a) => a.role === role.name).length;
    if (holders) return conflict(`${holders} ${holders === 1 ? "administrator still holds" : "administrators still hold"} ${role.label}. Give them another role first.`);
    adminRoles.splice(adminRoles.indexOf(role), 1);
    return ok(null, `${role.label} removed`);
  }],

  // Modules catalogue
  ["get", "admin/catalogue", () => catalogue()],
  ["post", "admin/catalogue/sync", () => {
    syncedAt = now();
    const rights = modules.reduce((n, m) => n + m.submodules.reduce((k, s) => k + s.rights.length, 0), 0);
    return ok({ modules_added: 0, submodules_added: 0, retired: 0, rights }, "The catalogue already matches the system");
  }],
  ["put", "admin/catalogue/modules/:id", ({ params, body }) => updateModule(params.id, body)],
  ["put", "admin/catalogue/submodules/:id", ({ params, body }) => updateSubmodule(params.id, body)],
];
